import { getContactLabel, getContactsSortedByRank } from '@/services/contact-point';
import { formatPhoneNumber, formatContactPointValue } from '@bariendo/lib/phone-validations';
import { ActionIcon, Menu, Text } from '@mantine/core';
import { IconDotsVertical, IconMail, IconPhone, IconStarFilled, IconEdit, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { UnifiedList } from '../shared/UnifiedList';
import { IdentityRow } from '../shared/IdentityRow';

type UnifiedContactListProps = {
  items: ReturnType<typeof getContactsSortedByRank>;
  onDeleteItem: (id: string) => Promise<void>;
  onEdit: (id: string, value: string, use: string, system: 'phone' | 'email', primary: boolean) => void;
};

export function UnifiedContactList({ items, onDeleteItem, onEdit }: UnifiedContactListProps) {
  const [menuOpenedId, setMenuOpenedId] = useState<string | null>(null);

  const contactItems = items
    .filter((contact) => !!contact.id)
    .map((contact) => ({
      value: formatContactPointValue(contact),
      displayValue:
        contact.system === 'phone'
          ? contact.value && contact.value.trim()
            ? formatPhoneNumber(contact.value)
            : (contact.value ?? '')
          : (contact.value ?? ''),
      label: getContactLabel(contact),
      id: contact.id!,
      primary: contact.rank === 1,
      system: contact.system as 'phone' | 'email',
    }))
    .sort((a, b) => {
      if (a.system === 'phone' && b.system === 'email') return -1;
      if (a.system === 'email' && b.system === 'phone') return 1;
      return 0;
    });

  if (contactItems.length === 0) {
    return (
      <Text c="rgba(0, 0, 0, 0.55)" size="sm" ta="center" py="md">
        No contact information
      </Text>
    );
  }

  return (
    <UnifiedList
      items={contactItems}
      renderItem={(item) => {
        const LeadingIcon = item.system === 'phone' ? IconPhone : IconMail;
        return (
          <IdentityRow
            leading={<LeadingIcon size={16} style={{ color: 'rgba(0, 0, 0, 0.55)' }} />}
            primary={item.displayValue}
            secondary={item.label || undefined}
            emphasized={item.primary}
            trailing={
              item.primary ? (
                <IconStarFilled
                  size={12}
                  style={{ color: 'rgba(0, 0, 0, 0.55)' }}
                  title={item.system === 'phone' ? 'Main Phone Number' : 'Main Email Address'}
                />
              ) : undefined
            }
          />
        );
      }}
      renderActions={(item) => (
        <Menu
          shadow="lg"
          width={160}
          position="bottom-end"
          radius="lg"
          opened={menuOpenedId === item.id}
          onChange={(opened) => {
            if (item.id) setMenuOpenedId(opened ? item.id : null);
          }}
        >
          <Menu.Target>
            <ActionIcon variant="subtle" size="xs">
              <IconDotsVertical size={14} color="var(--mantine-color-gray-9)" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown py="sm">
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={() => {
                if (item.id) onEdit(item.id, item.value, item.label, item.system, item.primary);
              }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              disabled={item.primary}
              onClick={() => {
                if (item.id && !item.primary) onDeleteItem(item.id);
              }}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    />
  );
}
